"use client";

import { useId } from "react";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart as RechartsRadarChart,
  ResponsiveContainer,
} from "recharts";
import { twMerge } from "tailwind-merge";

import { GrowinColors } from "@/colors";

import { RadarChartProps, TickInfoProps } from "./RadarChartProps";

export const DEFAULT_TICK_INFO = {
  passiveNameColor: "#CED3DF",
  activeNameColor: GrowinColors["data-primary"],
  focusNameColor: GrowinColors["data-primary"],
};

export const DEFAULT_RADAR_INFO = {
  dotFill: GrowinColors["data-primary"],
  dotRadius: 10,
  dotFontSize: 14,
  dotTextOffset: 5,
  gradientEnd: "#e2f7ff",
  gradientStart: "#5a8fd6",
};

interface TickProps {
  payload: { value: string };
  x: number;
  y: number;
  cx: number;
  cy: number;
  textAnchor: string;
}

export const RadarChart = ({
  data,
  tickInfo = DEFAULT_TICK_INFO,
  radarInfo = DEFAULT_RADAR_INFO,
  polarGridColor = "#F0F0F0",
  minValue = 0,
  maxValue = 5,
  lang = "zh",
  focusValues,
}: RadarChartProps) => {
  const id = useId();
  const newId = id?.replaceAll(":", "");

  if (!data || Object.keys(data).length === 0) return <></>;

  const radarData = Object.keys(data).map((key) => {
    const value = data[key] !== 0 ? data[key] : 0.1;
    return {
      name: key,
      value: value,
    };
  });
  const {
    dotRadius,
    dotFill,
    dotFontSize,
    dotTextOffset,
    gradientStart,
    gradientEnd,
  } = radarInfo;
  const polarRadiusTickCount = maxValue + 1;

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      id={newId}
      className="@container"
    >
      <RechartsRadarChart data={radarData} margin={{ left: 100 }}>
        <PolarGrid strokeWidth={3} stroke={polarGridColor} />
        <PolarAngleAxis
          dataKey="name"
          tick={(tickProps: TickProps) =>
            handleTick({
              data,
              tickProps,
              tickInfo,
              lang,
              focusValues,
            })
          }
        />
        <PolarRadiusAxis
          domain={[minValue, maxValue]}
          tickCount={polarRadiusTickCount}
          tick={false}
          axisLine={false}
        />
        <Radar
          isAnimationActive={false}
          dataKey="value"
          fill={`url('#gradient-${newId}')`}
          fillOpacity="0.7"
          dot={(props) => (
            <CustomDot
              {...props}
              // eslint-disable-next-line
              key={(props?.payload?.name ?? "") as string}
              radius={dotRadius}
              fill={dotFill}
              fontSize={dotFontSize}
              textOffset={dotTextOffset}
            />
          )}
        />
        <defs>
          <linearGradient
            id={`gradient-${newId}`}
            gradientTransform="rotate(90)"
          >
            <stop offset="5%" stopColor={gradientStart} />
            <stop offset="95%" stopColor={gradientEnd} />
          </linearGradient>
        </defs>
      </RechartsRadarChart>
    </ResponsiveContainer>
  );
};

const CustomDot = ({
  payload: { value: score },
  cx,
  cy,
  fill,
  radius = 10,
  fontSize = 14,
  textOffset = 5,
}: {
  payload: { value: number };
  cx: number;
  cy: number;
  fill: string;
  radius?: number;
  fontSize?: number;
  textOffset?: number;
}) => {
  if (score < 1) return <></>;
  return (
    <svg textAnchor="middle">
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill={fill}
        stroke="#fff"
        strokeWidth={1}
      />
      <text x={cx} y={cy + textOffset} fill="#fff" fontSize={fontSize}>
        {score}
      </text>
    </svg>
  );
};

const handleTick = ({
  lang,
  data,
  tickProps: {
    payload: { value: name },
    x,
    y,
    cx,
    cy,
    textAnchor,
  },
  tickInfo,
  focusValues,
}: {
  lang: RadarChartProps["lang"];
  data: RadarChartProps["data"];
  tickProps: TickProps;
  tickInfo: TickInfoProps;
  focusValues?: RadarChartProps["focusValues"];
}) => {
  const score = data.name;
  const passiveNameColor = tickInfo.passiveNameColor;
  const activeNameColor = tickInfo.activeNameColor;
  const focusNameColor = tickInfo.focusNameColor;

  return (
    <text
      y={y + (y - cy) / 6 - 15}
      x={x + (x - cx) / 10}
      textAnchor={textAnchor}
    >
      {name.split(" ").map((word: string, i: number) => (
        <tspan
          fill={
            score < 1
              ? passiveNameColor
              : focusValues?.includes(name)
              ? focusNameColor
              : activeNameColor
          }
          dy="1.1em"
          x={x + (x - cx) / 10}
          key={i}
          className={twMerge(
            "select-none font-bold",
            lang === "zh" ? "text-base" : "text-xs",
            focusValues?.includes(name)
              ? lang === "zh"
                ? "text-lg"
                : "text-sm"
              : ""
          )}
        >
          {word}
        </tspan>
      ))}
    </text>
  );
};
